<template>
  <v-container>
    <v-row>
      <v-col>
        <v-btn color="primary" @click="openModal('create')">Create Criteria</v-btn>
      </v-col>
    </v-row>
    <v-data-table-server
      v-model:items-per-page="pagination.limit"
      :items="criteria"
      :headers="headers"
      :items-length="count"
      :loading="loading"
      :search="pagination.q"
      item-key="id"
      @update:options="handleLimitChange">
      <template v-slot:[`item.description`]="{ item }">
        <div v-html="item.description"></div>
      </template>
      <template v-slot:[`item.action`]="{ item }">
        <v-btn router small color="primary" @click="openModal('edit', item)"> Edit </v-btn>
        <v-btn
          icon
          variant="text"
          :color="item.thubmnail ? 'primary' : 'secondary'"
          @click="openModal('editThumbnail', item)">
          <v-icon icon="mdi-image"></v-icon>
        </v-btn>
        <v-btn
          icon
          variant="text"
          :color="item.video ? 'primary' : 'secondary'"
          @click="openModal('editVideo', item)">
          <v-icon icon="mdi-video"></v-icon>
        </v-btn>
        <v-btn icon variant="text" color="primary" @click="openModal('delete', item)">
          <v-icon icon="mdi-delete"></v-icon>
        </v-btn>
      </template>
      <template v-slot:top>
        <v-text-field
          v-model="pagination.q"
          class="ma-2"
          density="compact"
          placeholder="Search criteria group..."
          hide-details></v-text-field>
      </template>
    </v-data-table-server>

    <v-dialog v-model="isCreateModalOpen">
      <CriteriaEditComponent
        :yearId="yearId"
        :criteria="new CriteriaDTO()"
        :isCreate="true"
        @criteriaEditComplete="closeModal"
        @criteriaEditCanceled="closeModal"
        @snack="throwSnack" />
      />
    </v-dialog>
    <v-dialog v-model="isEditModelOpen">
      <CriteriaEditComponent
        :yearId="yearId"
        :criteria="selectedCriteria"
        :isCreate="false"
        @criteriaEditComplete="closeModal"
        @criteriaEditCanceled="closeModal"
        @snack="throwSnack" />
      />
    </v-dialog>
    <v-dialog v-model="isEditAssetOpen">
      <AssetPreviewComponent
        :asset="selectedCriteria.thubmnail"
        type="image"
        :onUpload="handleFileUpload"
        @assetSaved="handleAssetSaved"
        @assetCanceled="closeModal"
        @snack="throwSnack" />
    </v-dialog>

    <v-dialog v-model="isVideoAssetOpen">
      <AssetPreviewComponent
        :asset="selectedCriteria.video"
        type="video"
        :onUpload="handleVideoUpload"
        @assetSaved="handleAssetSaved"
        @assetCanceled="closeModal"
        @snack="throwSnack" />
    </v-dialog>

    <ConfirmDeleteComponent
      :isOpen="isDeleteModelOpen"
      item="Criteria"
      description="Are you sure you want to delete this criteria?"
      @actionConfirmed="handleDeleteConfirmed"
      @actionCanceled="closeModal" />
  </v-container>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { CriteriaDTO, IAssetDTO, ICriteriaDTO, IPaginationDTO, PaginationDTO } from '@/dtos';
import type { VDataTableServer } from 'vuetify/components';
import { useCriteriaStore, useUpdateStatusStore } from '@/stores/modules';
import { SNACKSTATUS } from '@/stores/util';
import { useSnack } from '@/helpers';
import CriteriaEditComponent from './CriteriaEditComponent.vue';
import AssetPreviewComponent from '../ui/AssetPreviewComponent.vue';
import ConfirmDeleteComponent from '../ui/ConfirmDeleteComponent.vue';
import { uploadCriteriaMovie, uploadCriteriaThubmnail } from '@/controllers/admin';
const emit = defineEmits<{
  (e: 'snack', objType: string, snackType: SNACKSTATUS, err?: string): void;
}>();
const { throwSnack } = useSnack(emit);
interface Props {
  yearId: string;
}
const props = defineProps<Props>();
const yearId = computed(() => props.yearId);
const pagination = ref<IPaginationDTO>(new PaginationDTO());
const criteriaStore = useCriteriaStore();
const statusStore = useUpdateStatusStore();
const query = criteriaStore.getCriteriaForYearQuery(yearId, pagination);
const criteria = computed(() => query.data.value?.data ?? []);
const loading = computed(() => query.isLoading.value);
const count = computed(() => query.data?.value?.count ?? 0);
const isCreateModalOpen = ref<boolean>(false);
const isEditModelOpen = ref<boolean>(false);
const selectedCriteria = ref<ICriteriaDTO>(new CriteriaDTO());
const isDeleteModelOpen = ref<boolean>(false);
const isEditAssetOpen = ref<boolean>(false);
const isVideoAssetOpen = ref<boolean>(false);

const headers = [
  { title: 'Number', key: 'number' },
  { title: 'Group', key: 'group.group_name' },
  { title: 'name', key: 'name' },
  { title: 'description', key: 'description' },
  { title: 'Max', key: 'max_count' },
  { title: 'Edit', key: 'action' },
];

const handleLimitChange = (value: VDataTableServer) => {
  pagination.value.buildPagination(value);
};

const closeModal = async () => {
  isCreateModalOpen.value = false;
  isEditModelOpen.value = false;
  isVideoAssetOpen.value = false;
  isDeleteModelOpen.value = false;
  isEditAssetOpen.value = false;
  selectedCriteria.value = new CriteriaDTO();
};
const openModal = async (type: string, item?: ICriteriaDTO) => {
  if (item) {
    selectedCriteria.value = new CriteriaDTO(item);
  }
  if (type === 'create') {
    isCreateModalOpen.value = true;
  } else if (type === 'edit') {
    isEditModelOpen.value = true;
  } else if (type === 'editThumbnail') {
    isEditAssetOpen.value = true;
  } else if (type === 'editVideo') {
    isVideoAssetOpen.value = true;
  } else if (type === 'delete') {
    isDeleteModelOpen.value = true;
  }
};

const handleDeleteConfirmed = async () => {
  criteriaStore.removeCriteria.mutateAsync(selectedCriteria.value.id);
};

const handleFileUpload = async (
  file: File,
  updateProgress: (progress: number) => void,
): Promise<IAssetDTO> => {
  const response = await uploadCriteriaThubmnail(selectedCriteria.value.id, file, updateProgress);
  return response;
};

const handleVideoUpload = async (
  file: File,
  updateProgress: (progress: number) => void,
): Promise<IAssetDTO> => {
  const response = await uploadCriteriaMovie(selectedCriteria.value.id, file, updateProgress);
  return response;
};

const handleAssetSaved = async () => {
  await criteriaStore.invalidateCriteria();
};

const key = computed(() => `criteria-${selectedCriteria.value.id}`);
const status = computed(() => statusStore.getStatus(key.value));
watch(status, (value) => {
  if (!value) return;
  emit('snack', 'Criteria', value, statusStore.getError(key.value));
  closeModal();
});
</script>
